/** @jsx isolateComponent */

import { Fragment } from 'react';

import { AsyncIndicator } from '@zapier/common-components';
import { isolateComponent } from '@zapier/common-utils';
import { Link } from '@zapier/design-system';
import { useTrackingContext } from '@zapier/toolbox-tracking';
import { Price } from '@zapier/currency';

import { EVENT_TRACK_TRIAL_BANNER } from '@zapier/design-system-revenue/src/constants';
import { buildUpgradeUrl } from '@zapier/design-system-revenue/src/utils';

import { useData as _useData } from './hooks/useData';
import {
  Props as ShouldRenderProps,
  useShouldRender,
} from './hooks/useShouldRender';

type Props = {
  /** For dependency injection testing only */
  useData?: () => ReturnType<typeof _useData>;
};

const ProfessionalTrialBanner = ({ useData = _useData }: Props) => {
  const { error, isLoading, nextPlan, trialDaysRemaining } = useData();
  const { trackEvent } = useTrackingContext();

  if (isLoading) {
    return <AsyncIndicator />;
  }

  if (!nextPlan || !!error) {
    return <Fragment>{error}</Fragment>;
  }

  if (trialDaysRemaining > 30) {
    return (
      <div>You have {trialDaysRemaining} days left in your extended trial.</div>
    );
  }

  const onClick = () => {
    trackEvent(EVENT_TRACK_TRIAL_BANNER, {
      in_trial: true,
    });
  };

  return (
    <span id="trial-message">
      <span style={{ fontWeight: 600 }}>
        Yay! You are now trialing Professional.
      </span>{' '}
      You have {trialDaysRemaining} days to try out our{' '}
      <Link
        href="/help/account/billing-pricing/understand-your-zapier-premium-trial"
        onClick={onClick}
      >
        premium features
      </Link>{' '}
      or{' '}
      <Link
        href={buildUpgradeUrl('free', nextPlan.name, nextPlan.interval)}
        onClick={onClick}
      >
        upgrade now
      </Link>
      {nextPlan.monthlyAmount > -1 && (
        <span>
          {' '}
          for just <Price>{nextPlan.monthlyAmount}</Price>/month.
        </span>
      )}
    </span>
  );
};

ProfessionalTrialBanner.shouldRender = function shouldRender(
  props: ShouldRenderProps = {}
) {
  return useShouldRender.call(this, props);
};

export { ProfessionalTrialBanner };
